import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
    Box, Chip, FormControlLabel, IconButton, InputAdornment, MenuItem, Select, Switch, TextField, Tooltip, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ListAltIcon from '@mui/icons-material/ListAlt';
import {
    Bucket, RunRow, RunsResponse, executionStatsService, isAbort
} from '../../services/executionStatsService';
import { PanelError, PanelSkeleton, SectionCard } from '../Benchmark/BenchmarkPrimitives';
import { control, menu } from '../CaseWorkflow/filterStyles';
import { keepingNavState } from '../../utils/searchParamsNavigation';
import { useBucketColours, BUCKET_LABELS, bucketDefinition, count, relativeTime } from './executionStatsVocabulary';
import { rangeBounds } from './executionStatsWindows';
import { ErrorGroups, StatusPill, elapsed, when } from './runPresentation';
import { baseCode } from './runLogLinks';
import RunDetailDrawer from './RunDetailDrawer';
import { dsVar, ink } from '../../theme/tokens';

/**
 * Ranges for the run log. The API caps an org-wide list at 92 days, so there
 * is no 12-month option here as there is on the Overview.
 */
export const RUN_LOG_RANGES = [
    { key: '24h', label: 'Last 24 hours', days: 1 },
    { key: '7d', label: 'Last 7 days', days: 7 },
    { key: '30d', label: 'Last 30 days', days: 30 },
    { key: '90d', label: 'Last 90 days', days: 90 }
];
/** Seven days: long enough to see a pattern, short enough to load fast. */
const DEFAULT_RANGE = RUN_LOG_RANGES[1];

/** Chip order: what needs someone first. */
const CHIP_ORDER: Bucket[] = ['failed', 'stalled', 'running', 'pendingReview', 'success'];

const PAGE_SIZE = 25;
export const REFRESH_MS = 30000;

/** Every parameter the run log reads; the ones cleared by "Clear filters". */
const FILTER_PARAMS = ['range', 'from', 'to', 'code', 'campaign', 'versions', 'bucket', 'q', 'benchmarks'];

const isIso = (value: string | null): value is string => Boolean(value) && Number.isFinite(Date.parse(value as string));

const cell = { px: 1.5, py: 1, borderBottom: `1px solid ${dsVar('line')}`, fontSize: '0.8125rem', textAlign: 'left' as const, verticalAlign: 'top' as const };
const headCell = { ...cell, color: dsVar('fg-2'), fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' as const };

const shortId = (id: string) => (id.length > 10 ? `…${id.slice(-8)}` : id);

/**
 * Every workflow run in the org, newest first — the operations view.
 *
 * Filtered on the server, so the count is the real count and not a page's
 * worth. Every filter lives in the query string, so a view (or one run, with
 * `run=`) can be shared, and counts elsewhere link straight into it. A row
 * opens the run's steps in a docked drawer.
 */
const RunLog: React.FC<{ onDrawerChange?: (open: boolean) => void }> = ({ onDrawerChange }) => {
    const bucketColours = useBucketColours();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const patch = useCallback((changes: Record<string, string | null>) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value); else next.delete(key);
        });
        setSearchParams(next, keepingNavState(location.state, { replace: true }));
    }, [searchParams, setSearchParams, location.state]);
    const patchRef = useRef(patch);
    patchRef.current = patch;

    const range = RUN_LOG_RANGES.find((r) => r.key === searchParams.get('range')) ?? DEFAULT_RANGE;
    const fromParam = searchParams.get('from');
    const toParam = searchParams.get('to');
    const custom = isIso(fromParam) && isIso(toParam) && Date.parse(fromParam) < Date.parse(toParam)
        ? { from: fromParam, to: toParam }
        : null;
    const campaignId = searchParams.get('campaign') || null;
    const code = campaignId ? null : searchParams.get('code') || null;
    const groupVersions = searchParams.get('versions') !== 'separate';
    const bucketParam = searchParams.get('bucket') as Bucket | null;
    const bucket = bucketParam && CHIP_ORDER.includes(bucketParam) ? bucketParam : null;
    const q = (searchParams.get('q') || '').trim();
    const includeBenchmarks = searchParams.get('benchmarks') === 'include';
    const live = searchParams.get('live') === '1';
    const openRun = searchParams.get('run');
    const keyed = Boolean(code || campaignId);

    const filterKey = [
        range.key, custom?.from, custom?.to, campaignId, code, groupVersions, bucket, q, includeBenchmarks
    ].join('|');

    // The page belongs to the filters it was chosen under, so changing a filter
    // starts again at page 1 without a second request for the old page.
    const [paging, setPaging] = useState({ filterKey, page: 1 });
    const page = paging.filterKey === filterKey ? paging.page : 1;
    const setPage = (next: number) => setPaging({ filterKey, page: next });

    const [data, setData] = useState<RunsResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [tick, setTick] = useState(0);
    const [updatedAt, setUpdatedAt] = useState<number | null>(null);
    const [workflows, setWorkflows] = useState<{ key: string; name: string | null }[]>([]);
    const [searchInput, setSearchInput] = useState(q);
    const lastQuery = useRef<string | null>(null);

    useEffect(() => { onDrawerChange?.(Boolean(openRun)); }, [openRun, onDrawerChange]);

    // The box follows the URL (a link, or back); typing reaches the URL after a pause.
    useEffect(() => { setSearchInput(q); }, [q]);
    useEffect(() => {
        const text = searchInput.trim();
        if (text === q) return undefined;
        const timer = window.setTimeout(() => patchRef.current({ q: text || null }), 350);
        return () => window.clearTimeout(timer);
    }, [searchInput, q]);

    useEffect(() => {
        const queryKey = `${filterKey}|${page}|${attempt}`;
        // A timed refresh keeps the rows on screen; anything else starts clean.
        const refresh = lastQuery.current === queryKey;
        lastQuery.current = queryKey;
        if (!refresh) setData(null);
        setError(null);

        const controller = new AbortController();
        const bounds = custom ?? rangeBounds(range);
        executionStatsService
            .listRuns({
                scope: 'flow',
                keyType: campaignId ? 'campaign' : 'code',
                keys: campaignId ? [campaignId] : code ? [code] : [],
                groupVersions,
                ...bounds,
                bucket,
                q: q || null,
                includeBenchmarks,
                page,
                limit: PAGE_SIZE
            }, { signal: controller.signal })
            .then((res) => {
                setData(res);
                setUpdatedAt(Date.now());
            })
            .catch((err) => { if (!isAbort(err)) setError(err?.message || 'Could not load runs.'); });
        return () => controller.abort();
        // Every input is folded into filterKey.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterKey, page, attempt, tick]);

    useEffect(() => {
        if (!live) return undefined;
        const timer = window.setInterval(() => {
            if (document.visibilityState === 'visible') setTick((n) => n + 1);
        }, REFRESH_MS);
        return () => window.clearInterval(timer);
    }, [live]);

    // The workflows that ran in the range, for the picker, busiest first.
    useEffect(() => {
        const controller = new AbortController();
        const bounds = custom ?? rangeBounds(range);
        executionStatsService
            .getBreakdown({ scope: 'flow', keyType: 'code', groupVersions: true, ...bounds, sort: 'total', limit: 200 }, { signal: controller.signal })
            .then((res) => setWorkflows(res.rows.map((row) => ({ key: row.key, name: row.name }))))
            // The picker is a convenience: without it, the current workflow is still offered.
            .catch(() => undefined);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.key, custom?.from, custom?.to]);

    const workflowOptions = useMemo(() => {
        if (!code || workflows.some((w) => w.key === code)) return workflows;
        const base = workflows.find((w) => w.key === baseCode(code));
        return [{ key: code, name: base?.name ?? null }, ...workflows];
    }, [workflows, code]);

    const tracked = data?.tracked;
    const chips = CHIP_ORDER.filter((b) =>
        b === 'success' || b === 'failed'
        || (b === 'pendingReview' && tracked?.pendingReview)
        || ((b === 'running' || b === 'stalled') && (tracked ? tracked.running : true)));

    const hasFilters = Boolean(custom || keyed || bucket || q || includeBenchmarks || searchParams.get('range'));
    const clearFilters = () => patch(Object.fromEntries(FILTER_PARAMS.map((p) => [p, null])));

    const openRow = (run: RunRow) => { if (run.referenceId) patch({ run: run.referenceId }); };

    const rangeText = custom
        ? `${new Date(custom.from).toLocaleString()} – ${new Date(custom.to).toLocaleString()}`
        : range.label;

    const chip = (value: Bucket | null, label: string) => {
        const active = value === bucket;
        return (
            <Tooltip key={label} title={value && data ? bucketDefinition(value, data.staleAfterHours) : ''} arrow placement="top" describeChild>
                <Box
                    component="button"
                    aria-pressed={active}
                    onClick={() => patch({ bucket: value })}
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                        fontSize: '0.75rem', fontWeight: 600, px: 1.25, py: 0.5, borderRadius: '999px', cursor: 'pointer',
                        border: '1px solid', borderColor: active ? dsVar('accent-fg') : dsVar('line'),
                        color: active ? dsVar('on-accent') : dsVar('fg-soft'), backgroundColor: active ? dsVar('accent') : 'transparent'
                    }}
                >
                    {value && <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: bucketColours[value] }} />}
                    {label}
                </Box>
            </Tooltip>
        );
    };

    return (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
                <Box sx={{ maxWidth: '84rem', mx: 'auto', px: 4, py: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Filters */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                        <TextField
                            size="small"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Reference ID or run name"
                            inputProps={{ 'aria-label': 'Search runs', maxLength: 100 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: dsVar('fg-muted') }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ ...control, minWidth: 260, '.MuiInputBase-input': { color: dsVar('fg-soft'), fontSize: '0.8125rem' } }}
                        />
                        <Select
                            size="small"
                            value={custom ? 'custom' : range.key}
                            onChange={(e) => patch({ range: String(e.target.value) === DEFAULT_RANGE.key ? null : String(e.target.value), from: null, to: null })}
                            sx={control}
                            MenuProps={menu}
                            inputProps={{ 'aria-label': 'Time range' }}
                        >
                            {custom && <MenuItem value="custom" disabled>Custom range</MenuItem>}
                            {RUN_LOG_RANGES.map((r) => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
                        </Select>
                        {!campaignId && (
                            <Select
                                size="small"
                                value={code ?? ''}
                                displayEmpty
                                onChange={(e) => patch({ code: String(e.target.value) || null, versions: null })}
                                sx={{ ...control, minWidth: 220, maxWidth: 320 }}
                                MenuProps={menu}
                                inputProps={{ 'aria-label': 'Workflow' }}
                            >
                                <MenuItem value="">All workflows</MenuItem>
                                {workflowOptions.map((w) => (
                                    <MenuItem key={w.key} value={w.key}>{w.name || w.key}</MenuItem>
                                ))}
                            </Select>
                        )}
                        {campaignId && (
                            <Chip
                                label={`Campaign ${shortId(campaignId)}`}
                                title={campaignId}
                                onDelete={() => patch({ campaign: null })}
                                sx={{ color: dsVar('fg-body'), backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, '.MuiChip-deleteIcon': { color: dsVar('fg-muted') } }}
                            />
                        )}
                        {custom && (
                            <Chip
                                label={rangeText}
                                onDelete={() => patch({ from: null, to: null })}
                                sx={{ color: dsVar('fg-body'), backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, '.MuiChip-deleteIcon': { color: dsVar('fg-muted') } }}
                            />
                        )}
                        {!keyed && (
                            <Tooltip title="Benchmark backtests replay a workflow under a BENCHMARK_ code. They're left out unless you include them." arrow>
                                <FormControlLabel
                                    sx={{ ml: 0.5, '.MuiFormControlLabel-label': { fontSize: '0.8125rem', color: dsVar('fg-soft') } }}
                                    control={
                                        <Switch
                                            size="small"
                                            checked={includeBenchmarks}
                                            onChange={(e) => patch({ benchmarks: e.target.checked ? 'include' : null })}
                                        />
                                    }
                                    label="Benchmark runs"
                                />
                            </Tooltip>
                        )}
                        {hasFilters && (
                            <Box
                                component="button"
                                onClick={clearFilters}
                                sx={{ background: 'none', border: 'none', p: 0, cursor: 'pointer', fontSize: '0.75rem', color: dsVar('accent-fg') }}
                            >
                                Clear filters
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }} role="group" aria-label="Status">
                        {chip(null, 'All')}
                        {chips.map((b) => chip(b, BUCKET_LABELS[b]))}
                    </Box>

                    <SectionCard
                        title="Runs"
                        subtitle={data
                            ? `${count(data.pagination.total)} ${data.pagination.total === 1 ? 'run' : 'runs'} · ${rangeText} · newest first`
                            : rangeText}
                        icon={<ListAltIcon fontSize="small" />}
                        actions={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {updatedAt && (
                                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                        Updated {new Date(updatedAt).toLocaleTimeString()}
                                    </Typography>
                                )}
                                <Tooltip title={`Refresh every ${REFRESH_MS / 1000} seconds while this tab is open`} arrow>
                                    <FormControlLabel
                                        sx={{ m: 0, '.MuiFormControlLabel-label': { fontSize: '0.75rem', color: dsVar('fg-soft') } }}
                                        control={
                                            <Switch size="small" checked={live} onChange={(e) => patch({ live: e.target.checked ? '1' : null })} />
                                        }
                                        label="Live"
                                    />
                                </Tooltip>
                                <Tooltip title="Refresh now" arrow>
                                    <IconButton size="small" onClick={() => setTick((n) => n + 1)} aria-label="Refresh runs" sx={{ color: dsVar('fg-2') }}>
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    >
                        {error && <PanelError message={error} onRetry={() => setAttempt((n) => n + 1)} />}

                        {!error && !data && <PanelSkeleton height={240} />}

                        {!error && data && data.runs.length === 0 && (
                            <Box sx={{ py: 3 }}>
                                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted'), fontStyle: 'italic' }}>
                                    {hasFilters ? 'No runs match these filters.' : `No runs started in the ${range.label.toLowerCase()}.`}
                                </Typography>
                            </Box>
                        )}

                        {!error && data && data.runs.length > 0 && (
                            <>
                                {page === 1 && <ErrorGroups runs={data.runs} total={data.pagination.total} />}
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <Box component="th" sx={headCell}>Started</Box>
                                                <Box component="th" sx={headCell}>Workflow</Box>
                                                <Box component="th" sx={headCell}>Run</Box>
                                                <Box component="th" sx={headCell}>Campaign</Box>
                                                <Box component="th" sx={headCell}>Status</Box>
                                                <Box component="th" sx={headCell}>Duration</Box>
                                                <Box component="th" sx={headCell}>Error</Box>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.runs.map((run, index) => {
                                                const selected = Boolean(run.referenceId) && run.referenceId === openRun;
                                                const runName = run.instanceName && run.instanceName !== run.workflowName ? run.instanceName : null;
                                                return (
                                                    <Box
                                                        component="tr"
                                                        key={run.referenceId ?? `row-${index}`}
                                                        tabIndex={run.referenceId ? 0 : -1}
                                                        aria-selected={selected}
                                                        onClick={() => openRow(run)}
                                                        onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') openRow(run); }}
                                                        sx={{
                                                            cursor: run.referenceId ? 'pointer' : 'default',
                                                            backgroundColor: selected ? 'rgba(255, 222, 89, 0.06)' : 'transparent',
                                                            boxShadow: selected ? `inset 2px 0 0 ${dsVar('accent-fg')}` : 'none',
                                                            '&:hover': { backgroundColor: dsVar('fg-2', 0.05) },
                                                            '&:focus-visible': { outline: `1px solid ${dsVar('accent-fg')}`, outlineOffset: -1 }
                                                        }}
                                                    >
                                                        <Box component="td" sx={{ ...cell, whiteSpace: 'nowrap' }}>
                                                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-body') }} title={run.startedAt ?? undefined}>
                                                                {relativeTime(run.startedAt)}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '0.625rem', color: dsVar('fg-muted') }}>{when(run.startedAt)}</Typography>
                                                        </Box>
                                                        <Box component="td" sx={{ ...cell, maxWidth: 260 }}>
                                                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-body'), fontWeight: 600 }} noWrap title={run.workflowName ?? undefined}>
                                                                {run.workflowName || run.name || '—'}
                                                            </Typography>
                                                            {run.code && (
                                                                <Typography sx={{ fontSize: '0.625rem', color: dsVar('fg-muted'), fontFamily: '"JetBrains Mono", monospace' }} noWrap title={run.code}>
                                                                    {run.code}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box component="td" sx={{ ...cell, maxWidth: 220 }}>
                                                            <Typography sx={{ fontSize: '0.8125rem', color: runName ? dsVar('fg-soft') : dsVar('fg-faint') }} noWrap title={runName ?? undefined}>
                                                                {runName || '—'}
                                                            </Typography>
                                                            {run.referenceId && (
                                                                <Typography sx={{ fontSize: '0.625rem', color: dsVar('fg-faint'), fontFamily: '"JetBrains Mono", monospace' }} noWrap title={run.referenceId}>
                                                                    {run.referenceId}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box component="td" sx={cell}>
                                                            {run.campaignId ? (
                                                                <Tooltip title="Show only this campaign's runs" arrow describeChild>
                                                                    <Box
                                                                        component="button"
                                                                        onClick={(e: React.MouseEvent) => {
                                                                            e.stopPropagation();
                                                                            patch({ campaign: run.campaignId as string, code: null, versions: null });
                                                                        }}
                                                                        sx={{
                                                                            background: 'none', border: 'none', p: 0, cursor: 'pointer',
                                                                            fontSize: '0.75rem', fontFamily: '"JetBrains Mono", monospace', color: dsVar('fg-2'),
                                                                            '&:hover': { color: dsVar('accent-fg'), textDecoration: 'underline' }
                                                                        }}
                                                                    >
                                                                        {shortId(run.campaignId)}
                                                                    </Box>
                                                                </Tooltip>
                                                            ) : <Box component="span" sx={{ color: dsVar('fg-faint') }}>—</Box>}
                                                        </Box>
                                                        <Box component="td" sx={cell}>
                                                            <StatusPill bucket={run.bucket} status={run.status} />
                                                        </Box>
                                                        <Box
                                                            component="td"
                                                            sx={{
                                                                ...cell, whiteSpace: 'nowrap', fontSize: '0.75rem',
                                                                color: run.bucket === 'stalled' ? ink('#FBBF24') : dsVar('fg-2')
                                                            }}
                                                        >
                                                            {elapsed(run)}
                                                        </Box>
                                                        <Box component="td" sx={{ ...cell, maxWidth: 320 }}>
                                                            {run.errorMessage ? (
                                                                <Typography
                                                                    sx={{ fontSize: '0.6875rem', color: ink('#FCA5A5'), fontFamily: '"JetBrains Mono", monospace' }}
                                                                    noWrap
                                                                    title={run.errorMessage}
                                                                >
                                                                    {run.errorMessage.split('\n')[0]}
                                                                </Typography>
                                                            ) : <Box component="span" sx={{ color: dsVar('fg-faint') }}>—</Box>}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </tbody>
                                    </Box>
                                </Box>
                                {data.pagination.pages > 1 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                                        {([['← Newer', page - 1, page <= 1], ['Older →', page + 1, page >= data.pagination.pages]] as const).map(
                                            ([label, target, disabled]) => (
                                                <Box
                                                    key={label}
                                                    component="button"
                                                    disabled={disabled}
                                                    onClick={() => setPage(target)}
                                                    sx={{
                                                        fontSize: '0.75rem', color: disabled ? dsVar('fg-faint') : dsVar('accent-fg'), background: 'none',
                                                        border: 'none', cursor: disabled ? 'default' : 'pointer', p: 0
                                                    }}
                                                >
                                                    {label}
                                                </Box>
                                            )
                                        )}
                                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                            Page {page} of {count(data.pagination.pages)}
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}
                    </SectionCard>
                </Box>
            </Box>

            <RunDetailDrawer
                referenceId={openRun}
                onClose={() => patch({ run: null })}
                onShowWorkflow={(workflow) => patch({ code: workflow, campaign: null, versions: null, bucket: null, q: null, run: null })}
                onShowCampaign={(id) => patch({ campaign: id, code: null, versions: null, bucket: null, q: null, run: null })}
            />
        </Box>
    );
};

export default RunLog;
